import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import type { SurfaceAiBasis } from '../lib';

import styles from './AiBasisMark.module.scss';
import { AiMark } from './AiMark';

type Props = {
  basis: SurfaceAiBasis | null;
  // Shown with a matched value only.
  said?: string;
  // Shown on hover over a suggested value's tag.
  rationale?: string;
  // The clinician's instruction to "Complete with AI" holds the value.
  fromInstruction?: boolean;
};

// The AI mark of a block-note value with what it rests on: the words said
// for a matched value, "new" for an option the template lacks, an assumption
// tag in place of the mark until the clinician confirms it, a tag of its
// own for a value AI suggested for the case, and one for a value the
// clinician's instruction holds.
export const AiBasisMark: FC<Props> = ({
  basis,
  said,
  rationale,
  fromInstruction = false,
}) => {
  if (fromInstruction && basis !== 'assumed') {
    return (
      <span
        className={cn('p5', styles.instruction)}
        data-component="workspace-note-ai-instruction"
      >
        <FormattedMessage
          id="workspace.note.fromInstruction"
          defaultMessage="from your instruction"
        />
      </span>
    );
  }
  if (basis === 'assumed') {
    return (
      <span
        className={cn('p5', styles.assumed)}
        data-component="workspace-note-ai-assumed"
      >
        <FormattedMessage
          id="workspace.note.aiAssumed"
          defaultMessage="assumption"
        />
      </span>
    );
  }
  if (basis === 'suggested') {
    return (
      <span
        className={cn('p5', styles.suggested)}
        title={rationale || undefined}
        data-component="workspace-note-ai-suggested"
      >
        <FormattedMessage
          id="workspace.note.aiSuggested"
          defaultMessage="AI suggested"
        />
      </span>
    );
  }
  const tag =
    basis === 'matched' && said ? (
      <span
        className={cn('p2', styles.said)}
        title={said}
        data-component="workspace-note-ai-said"
      >
        <FormattedMessage
          id="workspace.note.aiSaid"
          defaultMessage="← {said}"
          values={{ said }}
        />
      </span>
    ) : basis === 'newOption' ? (
      <span
        className={cn('p5', styles.newOption)}
        data-component="workspace-note-ai-new-option"
      >
        <FormattedMessage
          id="workspace.note.aiNewOption"
          defaultMessage="new"
        />
      </span>
    ) : null;
  return (
    <>
      {tag && <>{tag} </>}
      <AiMark />
    </>
  );
};
